import { useCallback } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { Button, Text } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import { NodeToolbar, Position } from "@xyflow/react";
import {
  AnnotationNodeData,
  emitUserInteractionEventSetter,
  shouldShowStepIdsAtom,
  showActiveAnnotationNodeSetter,
  tourExitSetter,
  nodeIdToExplainAtom,
} from "@zapier/canvas-state";
import { StepId } from "../../../StepId";
import { EditAssetButton } from "../../../EditAssetButton";
import { CloseButton, EXIT_TOUR } from "./CloseButton";
import { NextButton } from "./NextButton";
import styles from "./AnnotationNode.module.css";
import { isDraftOrTemplateNodeAtomFamily } from "@zapier/canvas-state";

type BackButtonProps = { onPress: () => void };

function BackButton({ onPress }: BackButtonProps) {
  return (
    <Button
      key="back"
      variant="tertiary"
      size="small"
      onPress={onPress}
      aria-label="Go to previous step"
      iconPosition="start"
    >
      <Icon name="arrowBigLeft" size={20} color="GrayWarm8" />
    </Button>
  );
}

function Arrow() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="25"
      height="14"
      viewBox="0 0 25 14"
      fill="none"
    >
      <path
        d="M12.1843 13.2404L23.8633 13.2404L12.1843 1.5614L0.505371 13.2404L12.1843 13.2404Z"
        fill="#FFFDF9"
      />
      <path
        d="M13.6531 3.50563L23.5101 13.2404L24.8633 13.2404L24.8633 12.2404L24.0706 12.2404L13.6429 2.06461C12.8548 1.29556 11.5933 1.30822 10.8208 2.09294L0.831522 12.2404L0.0388221 12.2404L0.0388222 13.2404L1.39204 13.2404L10.8127 3.53559C11.5867 2.73819 12.8624 2.72473 13.6531 3.50563Z"
        fill="#D7D3C9"
      />
    </svg>
  );
}

const NEXT = "Next";
const DONE = "Done";
export const EXIT_INTERACTION_GOAL = "Exited tour ❌";

export function AnnotationNode({
  data: { header, description, stepNumber, totalSteps, isLastStep },
  parentId,
}: {
  data: AnnotationNodeData;
  parentId?: string;
}) {
  const shouldShowStepIds = useAtomValue(shouldShowStepIdsAtom);
  const showActiveAnnotationNode = useSetAtom(showActiveAnnotationNodeSetter);
  const emitUserInteractionEvent = useSetAtom(emitUserInteractionEventSetter);
  const tourExit = useSetAtom(tourExitSetter);
  const nodeIdToExplain = useAtomValue(nodeIdToExplainAtom);

  const handleOnPress = useCallback(
    (action: "back" | "next" | "done" | "exit") => {
      if (action === "next") {
        const nextStep = stepNumber + 1;

        emitUserInteractionEvent({
          eventAction: "click",
          interactionGoal: `Tour: clicked ${NEXT}`,
          rawText: NEXT,
          // Show completion based on where we're going
          optionSelected: `completion: ${(nextStep / totalSteps).toFixed(2)}`,
        });

        showActiveAnnotationNode(nextStep);
      }

      if (action === "back") {
        const prevStep = stepNumber - 1;

        emitUserInteractionEvent({
          eventAction: "click",
          interactionGoal: "Tour: clicked back button",
          rawText: "back",
          // For back, show which step we're going back to
          optionSelected: `returning to step ${prevStep}`,
        });

        showActiveAnnotationNode(prevStep);
      }

      if (action === "done" || action === "exit") {
        // Emit different events for explanations vs tours
        if (nodeIdToExplain) {
          // We're in explanation mode
          emitUserInteractionEvent({
            eventAction: "click",
            interactionGoal: "Closed explanation",
            rawText: DONE,
          });
        } else {
          // We're in tour mode
          emitUserInteractionEvent({
            eventAction: "click",
            interactionGoal:
              action === "done" ? "Finished tour ✅" : EXIT_INTERACTION_GOAL,
            rawText: action === "done" ? DONE : EXIT_TOUR,
            optionSelected: `completion: ${
              action === "done" ? "1.00" : (stepNumber / totalSteps).toFixed(2) // Show current progress
            }`,
          });
        }

        // Use unified exit setter which handles all cleanup
        tourExit(parentId);
      }
    },
    [
      stepNumber,
      totalSteps,
      showActiveAnnotationNode,
      emitUserInteractionEvent,
      tourExit,
      nodeIdToExplain,
      parentId,
    ],
  );

  const getButtons = () => {
    const showBackButton = stepNumber > 1;
    const isLastOrSingleStep = isLastStep || totalSteps === 1;

    // Check if parent node is draft or template
    const isParentDraftOrTemplateNode = useAtomValue(
      isDraftOrTemplateNodeAtomFamily(parentId),
    );

    return (
      <>
        {showBackButton && (
          <div className={styles.backButtonWrapper}>
            <BackButton onPress={() => handleOnPress("back")} />
          </div>
        )}
        <div className={styles.mainButtonWrapper}>
          {!isParentDraftOrTemplateNode && (
            <EditAssetButton nodeId={parentId!} />
          )}
          {isLastOrSingleStep ? (
            <Button
              key="done"
              variant="primary"
              size="compact"
              onPress={() => handleOnPress("done")}
            >
              {DONE}
            </Button>
          ) : (
            <NextButton onPress={() => handleOnPress("next")}>
              {NEXT}
            </NextButton>
          )}
        </div>
      </>
    );
  };

  return (
    <>
      {shouldShowStepIds && <StepId />}
      {/* Small transparent node as anchor */}
      <div className={styles.hiddenAnchorNode} />
      <NodeToolbar
        isVisible={true}
        position={Position.Top}
        offset={16}
        className="nopan"
      >
        <div className={styles.root} data-testid="annotation-node">
          {/* We want to force user to click "Done" instead of "Exit tour" */}
          {!isLastStep && (
            <div className={styles.closeButton}>
              <CloseButton onPress={() => handleOnPress("exit")} />
            </div>
          )}
          <div className={styles.textSection} data-testid="annotation-content">
            <Text type="Title" color="TextDefault">
              {header}
            </Text>
            <Text type="MinimalPrint1Multiline" color="TextWeaker">
              {description}
            </Text>
          </div>
          <div className={styles.footer}>{getButtons()}</div>
          <div className={styles.bottomArrow}>
            <Arrow />
          </div>
        </div>
      </NodeToolbar>
    </>
  );
}
